import type { HtmlParserPlugin, HtmlParserPlugins } from '~/plugins/dom-parser'
import { ELEMENT_NODE, SHOW_TEXT, TEXT_NODE } from '#shared/utils/dom'

export type ContentSource = 'ibexa' | 'epub' | 'jats' | 'biblio' | 'default'

export { normalizeJats } from './jats/normalizeJats'
/**
 * Detects list type from the marker in the first list item's content.
 * Returns 'roman', 'alpha', 'decimal', 'dash', or null if no marker detected.
 */
function detectListTypeFromContent(ol: Element, doc: Document): 'roman' | 'alpha' | 'decimal' | 'dash' | null {
  const firstLi = ol.querySelector(':scope > li')
  if (!firstLi) {
    return null
  }

  // Get the first text content
  const walker = doc.createTreeWalker(firstLi, SHOW_TEXT)
  const firstText = walker.nextNode()
  if (!firstText || !firstText.nodeValue) {
    return null
  }

  const textContent = firstText.nodeValue

  // Check for dash markers first (– or -)
  if (/^\s*«?\s?[–-]\s/.test(textContent)) {
    return 'dash'
  }

  // Extract marker pattern: matches a), b), i), ii), 1), etc. at start
  const markerMatch = textContent.match(/^\s*«?\s?([a-z]+|\d+)[.)]/i)
  if (!markerMatch) {
    return null
  }

  const marker = markerMatch[1]!.toLowerCase()

  // Check if it's a number
  if (/^\d+$/.test(marker)) {
    return 'decimal'
  }

  // Check if it's a valid roman numeral
  // Roman numerals only use: i, v, x, l, c, d, m
  // Valid patterns: i, ii, iii, iv, v, vi, vii, viii, ix, x, xi, xii, xiii, xiv, xv, etc.
  const romanPattern = /^m{0,3}(?:cm|cd|d?c{0,3})(?:xc|xl|l?x{0,3})(?:ix|iv|v?i{0,3})$/
  if (romanPattern.test(marker) && marker.length > 0) {
    // It's a valid roman numeral pattern
    // But single letters like 'i', 'v', 'x' could be either roman or alpha
    // Use context: if marker is only roman chars AND forms valid roman, treat as roman
    const onlyRomanChars = /^[ivxlcdm]+$/.test(marker)
    if (onlyRomanChars) {
      return 'roman'
    }
  }

  // Otherwise it's alphabetical
  if (/^[a-z]+$/.test(marker)) {
    return 'alpha'
  }

  return null
}

export const normalizeLawQuotes: HtmlParserPlugin = (doc: Document) => {
  const sitats = doc.querySelectorAll('.sitat')
  for (const el of sitats) {
    const blockquote = doc.createElement('blockquote')
    blockquote.innerHTML = el.innerHTML
    blockquote.setAttribute('data-normalized', 'true')
    el.replaceWith(blockquote)
  }
  return doc
}

export const normalizeLists: HtmlParserPlugin = (doc: Document) => {
  // index-list should render as dash list.
  // We normalize both ol/ul to ul.list-dash so one visual rule is used.
  const indexLists = doc.querySelectorAll('ol.index-list, ul.index-list')
  for (const list of indexLists) {
    if (list.tagName.toLowerCase() === 'ol') {
      const ul = doc.createElement('ul')
      ul.className = list.className
      ul.classList.remove('index-list')
      ul.classList.add('list-dash')
      ul.setAttribute('data-normalized', 'true')
      while (list.firstChild) {
        ul.appendChild(list.firstChild)
      }
      list.replaceWith(ul)
      continue
    }

    list.classList.remove('index-list')
    list.classList.add('list-dash')
    list.setAttribute('data-normalized', 'true')
  }

  // dash-list class -> Dash list (circle style is respected as-is)
  const dashLists = doc.querySelectorAll('ul.dash-list')
  for (const ul of dashLists) {
    ul.classList.remove('dash-list')
    ul.classList.add('list-dash')
    ul.setAttribute('data-normalized', 'true')
  }

  // Decimal leading zero -> Parentheses list (1)
  const decimalZeros = doc.querySelectorAll('ol[style*="decimal-leading-zero"]')
  for (const ol of decimalZeros) {
    ol.removeAttribute('style')
    ol.classList.add('list-decimal-paren')
    ol.setAttribute('data-normalized', 'true')
  }

  // HTML type attribute decimal lists (1) -> keep default decimal counter, strip markers
  const typeDecimalLists = doc.querySelectorAll('ol[type="1"]')
  for (const ol of typeDecimalLists) {
    ol.removeAttribute('type')
    ol.setAttribute('data-normalized', 'true')
  }

  // HTML type attribute alpha lists (a, A) -> list-lower-alpha
  const typeAlphaLists = doc.querySelectorAll('ol[type="a"], ol[type="A"]')
  for (const ol of typeAlphaLists) {
    ol.removeAttribute('type')
    ol.classList.add('list-lower-alpha')
    ol.setAttribute('data-normalized', 'true')
  }

  // Specific ordering matters: Handle parenthesized alpha lists first
  // Handles:
  // 1. Lower-latin style with data-parentes attribute (explicit parenthesized)
  // 2. lower-alpha-parenthesis class (legacy class for parenthesized alpha)
  // 3. lower-alpha-parenthesis1 and lower-alpha2 classes (legacy parenthesized variants)
  const parenLatins = doc.querySelectorAll('ol[style*="lower-latin"][data-parentes], ul[style*="lower-latin"][data-parentes], ol.lower-alpha-parenthesis, ul.lower-alpha-parenthesis, ol.lower-alpha-parenthesis1, ul.lower-alpha-parenthesis1, ol.lower-alpha2, ul.lower-alpha2, ol.alpha-paranthesis, ul.alpha-paranthesis, ol.alpha-parenthesis, ul.alpha-parenthesis')
  for (const list of parenLatins) {
    let ol = list
    if (list.tagName.toLowerCase() === 'ul') {
      const convertedOl = doc.createElement('ol')
      for (const attr of Array.from(list.attributes)) {
        convertedOl.setAttribute(attr.name, attr.value)
      }
      while (list.firstChild) {
        convertedOl.appendChild(list.firstChild)
      }
      list.replaceWith(convertedOl)
      ol = convertedOl
    }

    ol.removeAttribute('style')
    ol.removeAttribute('data-parentes')
    ol.classList.remove('lower-alpha-parenthesis', 'lower-alpha-parenthesis1', 'alpha-paranthesis', 'alpha-parenthesis')
    if (ol.classList.contains('unindented1')) {
      ol.classList.remove('unindented1')
      ol.classList.add('unindented')
    }
    ol.classList.add('list-lower-alpha-paren')
    ol.setAttribute('data-normalized', 'true')
  }

  // Handle custom-list1 by detecting type from content
  // custom-list1 represents non-parenthesized markers (1., a., i.) in source content.
  const customLists1 = doc.querySelectorAll('ol.custom-list1')
  for (const ol of customLists1) {
    ol.classList.remove('custom-list1')
    const detectedType = detectListTypeFromContent(ol, doc)
    if (detectedType === 'dash') {
      // Convert ol to ul with dash style
      const ul = doc.createElement('ul')
      ul.className = 'list-dash'
      ul.setAttribute('data-normalized', 'true')
      // Copy all children
      while (ol.firstChild) {
        ul.appendChild(ol.firstChild)
      }
      ol.replaceWith(ul)
    }
    else if (detectedType === 'roman') {
      ol.classList.add('list-lower-roman')
      ol.setAttribute('data-normalized', 'true')
    }
    else if (detectedType === 'alpha') {
      ol.classList.add('list-lower-alpha')
      ol.setAttribute('data-normalized', 'true')
    }
    else if (detectedType === null) {
      // No marker pattern detected: preserve text as-is and do not render generated counters.
      ol.classList.add('list-unstyled')
      ol.setAttribute('data-normalized', 'true')
    }
    else {
      // Decimal markers in custom-list1 use the default decimal list style.
      ol.setAttribute('data-normalized', 'true')
    }
  }

  // Handle custom-list2 by detecting type from content
  const customLists2 = doc.querySelectorAll('ol.custom-list2')
  for (const ol of customLists2) {
    ol.classList.remove('custom-list2')
    const detectedType = detectListTypeFromContent(ol, doc)
    if (detectedType === 'dash') {
      // Convert ol to ul with dash style
      const ul = doc.createElement('ul')
      ul.className = 'list-dash'
      ul.setAttribute('data-normalized', 'true')
      // Copy all children
      while (ol.firstChild) {
        ul.appendChild(ol.firstChild)
      }
      ol.replaceWith(ul)
    }
    else if (detectedType === 'roman') {
      ol.classList.add('list-lower-roman-paren')
      ol.setAttribute('data-normalized', 'true')
    }
    else if (detectedType === 'decimal') {
      ol.classList.add('list-decimal-paren')
      ol.setAttribute('data-normalized', 'true')
    }
    else {
      // Default to alpha for 'alpha' or null
      ol.classList.add('list-lower-alpha-paren')
      ol.setAttribute('data-normalized', 'true')
    }
  }

  // Then general lower-latin (excluding ones we just processed)
  // Handles:
  // 1. Lower-latin style (excluding already processed parenthesized lists)
  // 2. lower-alpha class (generic alpha lists, excluding already processed)
  // 3. lower-alpha1/lower-apha1 classes (legacy non-parenthesized variants)
  const latins = doc.querySelectorAll('ol[style*="lower-latin"]:not(.list-lower-alpha-paren), ol.lower-alpha:not(.list-lower-alpha-paren), ol.lower-alpha1, ol.lower-apha1')
  for (const ol of latins) {
    ol.removeAttribute('style')
    ol.classList.remove('lower-alpha', 'lower-alpha1', 'lower-apha1')
    ol.classList.add('list-lower-alpha')
    ol.setAttribute('data-normalized', 'true')
  }

  // Lower Roman
  const romans = doc.querySelectorAll('ol[style*="lower-roman"], ol.lower-roman-parenthesis2')
  for (const ol of romans) {
    ol.removeAttribute('style')
    ol.classList.remove('lower-roman-parenthesis2')
    ol.classList.add('list-lower-roman')
    ol.setAttribute('data-normalized', 'true')
  }

  // Upper Roman -> legacy override to lower-roman-paren
  const upperRomans = doc.querySelectorAll('ol[style*="upper-roman"]')
  for (const ol of upperRomans) {
    ol.removeAttribute('style')
    ol.classList.add('list-lower-roman-paren')
    ol.setAttribute('data-normalized', 'true')
  }

  // Lower Roman Parenthesis class -> list-lower-roman-paren
  const lowerRomanParens = doc.querySelectorAll('ol.lower-roman-parenthesis, ol.lower-roman-parenthesis1')
  for (const ol of lowerRomanParens) {
    ol.classList.remove('lower-roman-parenthesis', 'lower-roman-parenthesis1')
    ol.classList.add('list-lower-roman-paren')
    ol.setAttribute('data-normalized', 'true')
  }

  // Handle start attribute for all ordered lists
  // CSS counters ignore the HTML start attribute, so we need to set counter-reset inline
  const olsWithStart = doc.querySelectorAll('ol[start]')
  for (const ol of olsWithStart) {
    const startValue = Number.parseInt(ol.getAttribute('start') || '1', 10)
    if (startValue > 1) {
      // counter-reset value is one less than start because counter-increment happens before display
      const existingStyle = ol.getAttribute('style') || ''
      const counterReset = `counter-reset: list-counter ${startValue - 1};`
      ol.setAttribute('style', existingStyle ? `${existingStyle} ${counterReset}` : counterReset)
      ol.setAttribute('data-normalized', 'true')
    }
  }

  // Strip redundant list markers from list item content
  // These are markers already in the text that duplicate the CSS-generated markers
  // Matches: a), b), i), ii), 1), 2), a., b., i., 1. etc. at the start of text
  // Also matches single letters followed by space before uppercase (e.g., "a Internasjonale")
  // Also handles optional leading guillemet «
  const listMarkerPattern = /^(«\s*)?([a-z]{1,4}|[ivxlcdm]+|\d+)[.)]\s*/i
  // Note: Includes Norwegian uppercase characters (Æ, Ø, Å) in lookahead
  const singleLetterMarkerPattern = /^([a-z])\s+(?=[A-ZÆØÅ])/

  function stripListMarker(text: string): string {
    let stripped = text.replace(listMarkerPattern, '')
    if (text === stripped) {
      stripped = text.replace(singleLetterMarkerPattern, '')
    }
    return stripped
  }

  const normalizedOls = doc.querySelectorAll('ol[data-normalized="true"]')
  for (const ol of normalizedOls) {
    const items = ol.querySelectorAll(':scope > li')
    for (const li of items) {
      const firstChild = li.firstChild
      if (firstChild && firstChild.nodeType === TEXT_NODE && firstChild.nodeValue) {
        // Text node - strip leading marker
        const original = firstChild.nodeValue
        const stripped = stripListMarker(original)
        if (original !== stripped) {
          firstChild.nodeValue = stripped
        }
      }
      else if (firstChild && firstChild.nodeType === ELEMENT_NODE) {
        // Element node - check if first text descendant has marker
        const walker = doc.createTreeWalker(li, SHOW_TEXT)
        const firstText = walker.nextNode()
        if (firstText && firstText.nodeValue) {
          const original = firstText.nodeValue
          const stripped = stripListMarker(original)
          if (original !== stripped) {
            firstText.nodeValue = stripped
          }
        }
      }
    }
  }

  // Strip redundant dash markers from unordered list items
  // Matches: – or - at the start of text (with optional leading guillemet)
  const dashMarkerPattern = /^(«\s*)?[–-]\s*/
  const normalizedUls = doc.querySelectorAll('ul[data-normalized="true"]')
  for (const ul of normalizedUls) {
    const items = ul.querySelectorAll(':scope > li')
    for (const li of items) {
      const firstChild = li.firstChild
      if (firstChild && firstChild.nodeType === TEXT_NODE && firstChild.nodeValue) {
        const original = firstChild.nodeValue
        const stripped = original.replace(dashMarkerPattern, '')
        if (original !== stripped) {
          firstChild.nodeValue = stripped
        }
      }
      else if (firstChild && firstChild.nodeType === ELEMENT_NODE) {
        const walker = doc.createTreeWalker(li, SHOW_TEXT)
        const firstText = walker.nextNode()
        if (firstText && firstText.nodeValue) {
          const original = firstText.nodeValue
          const stripped = original.replace(dashMarkerPattern, '')
          if (original !== stripped) {
            firstText.nodeValue = stripped
          }
        }
      }
    }
  }

  return doc
}

export const normalizePetitBlockquotes: HtmlParserPlugin = (doc: Document) => {
  // Handle blockquote.small and blockquote.bi (both should become petit paragraphs)
  const petitBlockquotes = doc.querySelectorAll('blockquote.small, blockquote.bi')
  for (const bq of petitBlockquotes) {
    const paragraphs = bq.querySelectorAll(':scope > p')
    if (paragraphs.length > 0) {
      // Add petit class to all direct child paragraphs
      for (const p of paragraphs) {
        p.classList.add('petit')
        p.setAttribute('data-normalized', 'true')
      }
      // Replace blockquote with its children
      bq.replaceWith(...bq.childNodes)
    }
    else {
      // No paragraphs, wrap content in a new paragraph
      const p = doc.createElement('p')
      p.className = 'petit'
      p.innerHTML = bq.innerHTML
      p.setAttribute('data-normalized', 'true')
      bq.replaceWith(p)
    }
  }

  // Handle p.extract (add petit class)
  const extractParagraphs = doc.querySelectorAll('p.extract')
  for (const p of extractParagraphs) {
    p.classList.add('petit')
    p.setAttribute('data-normalized', 'true')
  }

  // If a list immediately follows a petit paragraph, the list should also render as petit.
  // This also naturally covers lists surrounded by petit paragraphs.
  const lists = doc.querySelectorAll('ol, ul')
  for (const list of lists) {
    const prev = list.previousElementSibling
    if (prev?.matches('p.petit')) {
      list.classList.add('petit')
      list.setAttribute('data-normalized', 'true')
    }
  }

  return doc
}

export const normalizeLiteratureText: HtmlParserPlugin = (doc: Document) => {
  const walker = doc.createTreeWalker(doc.body, SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    if (node.nodeValue) {
      const original = node.nodeValue
      const replaced = node.nodeValue.replace(/KAPITTEL/gi, 'Kapittel')
      if (original !== replaced) {
        node.nodeValue = replaced
        node.parentElement?.setAttribute('data-normalized', 'true')
      }
    }
    node = walker.nextNode()
  }
  return doc
}

export const normalizeBrToParagraphs: HtmlParserPlugin = (doc: Document) => {
  const children = Array.from(doc.body.childNodes)
  if (children.length === 0) {
    return doc
  }

  const { ELEMENT_NODE, TEXT_NODE } = doc

  const isBr = (node: Node) => node.nodeType === ELEMENT_NODE && (node as Element).tagName === 'BR'

  const isWhitespace = (node: Node) => node.nodeType === TEXT_NODE && !(node.nodeValue ?? '').trim()

  const groups: Node[][] = [[]]
  let brStreak = 0

  for (const node of children) {
    if (isBr(node)) {
      brStreak++
      continue
    }
    if (brStreak > 0 && isWhitespace(node)) {
      continue
    }

    if (brStreak >= 2 && groups[groups.length - 1]!.length > 0) {
      groups.push([])
    }
    else if (brStreak === 1) {
      groups[groups.length - 1]!.push(doc.createTextNode(' '))
    }
    brStreak = 0
    groups[groups.length - 1]!.push(node)
  }

  const fragment = doc.createDocumentFragment()

  for (const group of groups) {
    const nodes: Node[] = []
    for (const node of group) {
      const prev = nodes[nodes.length - 1]
      if (node.nodeType === TEXT_NODE && prev?.nodeType === TEXT_NODE) {
        prev.nodeValue = (prev.nodeValue ?? '') + (node.nodeValue ?? '')
      }
      else {
        nodes.push(node)
      }
    }

    for (const node of nodes) {
      if (node.nodeType === TEXT_NODE) {
        node.nodeValue = (node.nodeValue ?? '').replace(/\s+/g, ' ')
      }
    }

    let start = 0
    let end = nodes.length - 1

    while (start <= end && nodes[start]!.nodeType === TEXT_NODE) {
      const trimmed = (nodes[start]!.nodeValue ?? '').replace(/^\s+/, '')
      nodes[start]!.nodeValue = trimmed
      if (trimmed) {
        break
      }
      start++
    }
    while (end >= start && nodes[end]!.nodeType === TEXT_NODE) {
      const trimmed = (nodes[end]!.nodeValue ?? '').replace(/\s+$/, '')
      nodes[end]!.nodeValue = trimmed
      if (trimmed) {
        break
      }
      end--
    }

    if (start > end) {
      continue
    }

    const p = doc.createElement('p')
    for (let k = start; k <= end; k++) {
      p.appendChild(nodes[k]!)
    }
    fragment.appendChild(p)
  }

  doc.body.replaceChildren(fragment)
  return doc
}

export const sourcePlugins: Record<ContentSource, HtmlParserPlugins> = {
  default: [],
  ibexa: [normalizeLawQuotes, normalizeLists],
  epub: [normalizeLiteratureText, normalizeLists, normalizePetitBlockquotes],
  // Normalize jats content to match structure defined in BodyText component
  jats: ['normalize-jats'],
  biblio: [normalizeBrToParagraphs],
}
